import paper from "paper";
import type { SegmentList } from "../tools/pathDeletion";

/** Total length (in project mm) of a path described by SVG pathData.
 *  Used by the slice tool to compare piece lengths when deciding
 *  which piece is the "main part" at a closed-loop's first cut.
 *  Returns 0 when the path can't be parsed. */
export function measurePathDataLength(
  pathData: string,
  scope: paper.PaperScope,
): number {
  let p: paper.CompoundPath | null = null;
  try {
    p = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return 0;
  }
  const len = p.length || 0;
  p.remove();
  return len;
}

/** First anchor point of the first subpath in a pathData string —
 *  the path's start endpoint. Returns null on parse failure or
 *  empty paths. Used by the slice tool's alternating selection
 *  rule to align piece endpoints with the cut's start position
 *  and with the first-cut intersection point on a closed loop. */
export function getPathFirstPoint(
  pathData: string,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  let p: paper.CompoundPath | null = null;
  try {
    p = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subs: paper.Path[] =
    p.children && p.children.length > 0
      ? (p.children as paper.Path[])
      : [p as unknown as paper.Path];
  const first = subs[0]?.segments[0];
  if (!first) {
    p.remove();
    return null;
  }
  const out = { x: first.point.x, y: first.point.y };
  p.remove();
  return out;
}

/** Last anchor point of the last subpath in a pathData string —
 *  the path's end endpoint. Returns null on parse failure or empty
 *  paths. Paired with {@link getPathFirstPoint} to identify each
 *  piece's two endpoints. */
export function getPathLastPoint(
  pathData: string,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  let p: paper.CompoundPath | null = null;
  try {
    p = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subs: paper.Path[] =
    p.children && p.children.length > 0
      ? (p.children as paper.Path[])
      : [p as unknown as paper.Path];
  const lastSub = subs[subs.length - 1];
  const last = lastSub?.segments[lastSub.segments.length - 1];
  if (!last) {
    p.remove();
    return null;
  }
  const out = { x: last.point.x, y: last.point.y };
  p.remove();
  return out;
}

/** Parse `pathData` into a single subpath's `SegmentList` (the
 *  shape the pure deletion helpers consume). Returns null when
 *  the data can't be parsed or the requested subpath is missing. */
export function parseSubpathToSegmentList(
  pathData: string,
  pathIndex: number,
  scope: paper.PaperScope,
): SegmentList | null {
  let tmp: paper.CompoundPath;
  try {
    tmp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subpaths: paper.Path[] =
    tmp.children && tmp.children.length > 0
      ? (tmp.children as paper.Path[])
      : [tmp as unknown as paper.Path];
  const sub = subpaths[pathIndex];
  if (!sub) {
    tmp.remove();
    return null;
  }
  const list: SegmentList = {
    closed: !!sub.closed,
    segments: sub.segments.map((s) => ({
      point: { x: s.point.x, y: s.point.y },
      handleIn: { x: s.handleIn.x, y: s.handleIn.y },
      handleOut: { x: s.handleOut.x, y: s.handleOut.y },
    })),
  };
  tmp.remove();
  return list;
}

/** Serialise a `SegmentList` back to SVG `pathData` via paper.js
 *  so the format matches what every other commit path produces.
 *  Builds a fresh `paper.Path` with the segment + handle data,
 *  reads `.pathData`, then disposes the temp item. */
export function segmentListToPathData(
  list: SegmentList,
  scope: paper.PaperScope,
): string {
  const path = new scope.Path();
  for (const s of list.segments) {
    path.add(
      new scope.Segment(
        new scope.Point(s.point.x, s.point.y),
        new scope.Point(s.handleIn.x, s.handleIn.y),
        new scope.Point(s.handleOut.x, s.handleOut.y),
      ),
    );
  }
  path.closed = list.closed;
  const data = path.pathData;
  path.remove();
  return data;
}

/** Split `pathData`'s `pathIndex` subpath at `cutPoint` and return
 *  the geometry with only `keepSide` of the cut retained (the other
 *  half — the dangling stub — dropped). Returns `null` for a
 *  degenerate cut (missing subpath, or a cut sitting on an
 *  endpoint, where nothing hangs past). Shared by the single-tap
 *  prune and the swipe-delete batch (issue #670). Pure geometry —
 *  takes only its arguments (extracted from ConstructionTool in
 *  #836). */
export function prunedPathData(
  scope: paper.PaperScope,
  pathData: string,
  pathIndex: number,
  cutPoint: { x: number; y: number },
  keepSide: "left" | "right",
): string | null {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subpaths: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  const targetSub = subpaths[pathIndex];
  if (!targetSub) {
    cp.remove();
    return null;
  }
  const loc = (targetSub as paper.Path).getNearestLocation(
    new scope.Point(cutPoint.x, cutPoint.y),
  );
  if (!loc) {
    cp.remove();
    return null;
  }
  // Bail on a cut that sits on an endpoint — nothing hangs past it,
  // so the prune would either be a no-op or strand a 1-anchor stub.
  const EPS = 1e-3;
  const L = targetSub.length;
  if (loc.offset < EPS || loc.offset > L - EPS) {
    cp.remove();
    return null;
  }
  // splitAt mutates `targetSub` to the left half and auto-inserts the
  // right half as a sibling in `cp`. Drop whichever half is the stub.
  const right = (targetSub as paper.Path).splitAt(loc);
  if (keepSide === "left") {
    if (right && right !== targetSub) (right as paper.Path).remove();
  } else {
    // Keep the right half; the original left half is the stub.
    (targetSub as paper.Path).remove();
  }
  const newPathData = cp.pathData;
  cp.remove();
  return newPathData || null;
}

/** Compare two path-data strings for geometric equivalence by
 *  re-serialising both through paper.js so format differences (the
 *  hand-rolled `M..L..L..Z` from `rebuildPrimitivePath` vs paper's
 *  own output from a parsed CompoundPath) don't cause false
 *  negatives in the round-trip restore check. */
export function pathDataEquivalent(
  a: string,
  b: string,
  scope: paper.PaperScope,
): boolean {
  let pa: paper.CompoundPath | null = null;
  let pb: paper.CompoundPath | null = null;
  try {
    pa = new scope.CompoundPath({ pathData: a, insert: false });
    pb = new scope.CompoundPath({ pathData: b, insert: false });
    return pa.pathData === pb.pathData;
  } catch {
    return false;
  } finally {
    pa?.remove();
    pb?.remove();
  }
}
